<template>
  <CBreadcrumb v-bind="props">
    <slot></slot>
  </CBreadcrumb>
</template>

<script>
export default {
  name: 'SimpleBreadcrumbRouter',
  props: {
    addClasses: [String, Array, Object],
    addLinkClasses: [String, Array, Object],
    addLastItemClasses: [String, Array, Object]
  },
  computed: {
    items() {
      let breadcrumbItems = []
      
      // Build breadcrumbs from route hierarchy
      const routes = this.$route.matched
      
      for (let i = 0; i < routes.length; i++) {
        const route = routes[i]
        
        // Skip routes without meta
        if (!route.meta) continue
        
        // Skip if no breadcrumb and no label (nothing to show)
        if (!route.meta.breadcrumb && !route.meta.label) continue
        
        // Context object passed to breadcrumb functions
        const context = {
          route: this.$route,
          store: this.$store,
          gettext: this.$gettext.bind(this),
          parent: i > 0 ? routes[i - 1] : null,
          parentBreadcrumbs: breadcrumbItems.slice() // Copy of parent breadcrumbs
        }
        
        let routeBreadcrumbs = []
        
        // Handle different breadcrumb meta types
        if (typeof route.meta.breadcrumb === 'function') {
          // Function that returns breadcrumb items
          const result = route.meta.breadcrumb(context)
          if (Array.isArray(result)) {
            routeBreadcrumbs = result
          } else if (result && typeof result === 'object') {
            routeBreadcrumbs = [result]
          }
        } else if (typeof route.meta.breadcrumb === 'object') {
          // Single breadcrumb object
          const breadcrumb = { ...route.meta.breadcrumb }
          
          // Process dynamic text
          if (typeof breadcrumb.text === 'function') {
            breadcrumb.text = breadcrumb.text(context)
          }
          
          // Process dynamic link
          if (typeof breadcrumb.to === 'function') {
            breadcrumb.to = breadcrumb.to(context)
          } else if (breadcrumb.to && !breadcrumb.to.params) {
            // Auto-fill params from current route if not specified
            breadcrumb.to = {
              ...breadcrumb.to,
              params: this.$route.params
            }
          }
          
          routeBreadcrumbs = [breadcrumb]
        } else if (typeof route.meta.breadcrumb === 'string') {
          // Simple string text
          routeBreadcrumbs = [{
            text: this.$gettext(route.meta.breadcrumb)
          }]
        }
        // Fallback: if no breadcrumb but has label, use label
        else if (!route.meta.breadcrumb && route.meta.label) {
          let text = route.meta.label
          
          // Special handling for specific routes
          if (route.name === 'eventIndex') {
            text = this.$store.getters['event/currentEventName'] || 'Event'
            routeBreadcrumbs = [{
              text: text,
              to: { name: 'picaServiceRedirect', params: this.$route.params }
            }]
          } else if (route.name === 'gallery') {
            text = this.$store.getters['eventGallery/gallery']?.name || 'Gallery'
            routeBreadcrumbs = [{
              text: text
            }]
          } else {
            // For all other routes with label, show them in breadcrumb
            routeBreadcrumbs = [{
              text: this.$gettext(text)
            }]
          }
        }
        
        // Default behavior is append, only replace if explicitly specified
        if (route.meta.breadcrumbMode === 'replace') {
          // Replace all breadcrumbs (for special cases with different param structure)
          breadcrumbItems = routeBreadcrumbs
        } else {
          // Default: append to parent breadcrumbs
          breadcrumbItems = [...breadcrumbItems, ...routeBreadcrumbs]
        }
      }
      
      // Always ensure Dashboard is at the beginning if not already there
      if (!breadcrumbItems.some(item => item.to && item.to.name === 'index')) {
        breadcrumbItems.unshift({
          to: { name: 'index' },
          text: this.$gettext('Dashboard'),
          addClasses: 'is-index'
        })
      }
      
      // Process items to ensure proper clickability
      breadcrumbItems = breadcrumbItems.map((item, index) => {
        const isLast = index === breadcrumbItems.length - 1
        const isDashboard = item.to && item.to.name === 'index'
        
        // Dashboard should always be clickable
        if (isDashboard) {
          return item
        }
        
        // Last item should never be clickable (current page)
        if (isLast && item.to) {
          return { ...item, to: undefined }
        }
        
        // Items without 'to' should not be clickable but should show as text
        if (!item.to) {
          return { ...item, to: undefined }
        }
        
        return item
      })

      return breadcrumbItems
    },
    props() {
      return {
        items: this.items,
        addClasses: this.addClasses,
        addLinkClasses: this.addLinkClasses,
        addLastItemClasses: this.addLastItemClasses
      }
    }
  }
}
</script>